import { BaseResponse } from '../data/types';
import { User } from '../users/user';

interface ConversionParameters {
  laboratory_ift: number;
  laboratory_angle: number;
  ift_cos_lab: number | null;
  reservoir_ift: number;
  reservoir_angle: number;
  ift_cos_res: number | null;
  oil_density: number;
}

enum System {
  AIRBRINE = 'Air-Brine',
  AIROIL = 'Air-Oil',
  OILBRINE = 'Oil-Brine',
  AIRHG = 'Air-Hg'
}

export enum AnalyzeType {
  LIAO = 'LIAO',
  LAMBDA = 'LAMBDA'
}

// TODO: Improve this name
export interface TendencyTypeProps {
  r_squared: number;
  line_sorted: number[];
  x_values_sorted: number[];
  equation: string;
}

export interface TendencyType {
  linear: TendencyTypeProps;
  power: TendencyTypeProps;
  logarithmic: TendencyTypeProps;
  exponential: TendencyTypeProps;
  selected: SelectedFziFunctionsOptions | null;
}

interface Values {
  pce: number | null;
  swir: number | null;
  best_a: number | null;
  best_b: number | null;
  lambda_variable: number | null;
  true_pc_max: number | null;
  no_w: number | null;
  nw: number | null;
  krw_absolute: number | null;
  sor_w: number | null;
  kro_absolute: number | null;
  /** Gas-Oil oil endpoint. Old reports may omit it and reuse kro_absolute. */
  kro_absolute_g?: number | null;
  no_g: number | null;
  ng: number | null;
  krg_absolute: number | null;
  sgc: number | null;
  sor: number | null;
  sgmax: number | null;
  sor_g: number | null;
  pc_max_og: number | null;
  pe: number | null;
  lambda_value: number | null;
}

export interface ZoneValues {
  liao_values: Values | null;
  lambda_values: Values | null;
}

export interface Curves {
  sw: number[];
  krw: number[];
  krow: number[];
  pcow: number[];
  height: number[];
  krg: number[];
  krog: number[];
  sg: number[];
  krw_scaled: number[];
  krow_scaled: number[];
  krg_scaled: number[];
  krog_scaled: number[];
}

export interface ZoneCurves {
  lambda_curves: Curves | null;
  liao_curves: Curves | null;
}

export interface Zone {
  uid: string;
  start: number;
  end: number;
  mean: number;
  color: string | null;
  zone_values: ZoneValues | null;
  zone_curves: ZoneCurves | null;
  zone_values_by_x_axis?: FziFunctionsByXAxisValue<ZoneValues | null> | null;
  zone_curves_by_x_axis?: FziFunctionsByXAxisValue<ZoneCurves | null> | null;
}

export interface FziFunctions {
  best_a: TendencyType | null;
  best_b: TendencyType | null;
  lambda_variable: TendencyType | null;
  true_pc_max: TendencyType | null;
  swir: TendencyType | null;
  pce: TendencyType | null;
  sor_w: TendencyType | null;
  kro_absolute: TendencyType | null;
  kro_absolute_g?: TendencyType | null;
  krw_absolute: TendencyType | null;
  no_w: TendencyType | null;
  nw: TendencyType | null;
  krg_absolute: TendencyType | null;
  ng: TendencyType | null;
  no_g: TendencyType | null;
  sor_g: TendencyType | null;
  sgc: TendencyType | null;
  sor: TendencyType | null;
  pe: TendencyType | null;
  lambda_value: TendencyType | null;
}

export type XAxisVariable = 'fzi' | 'rqi';

export type FziFunctionsByXAxisValue<T> = {
  fzi?: T;
  rqi?: T;
};

export type FziFunctionsByXAxis = FziFunctionsByXAxisValue<FziFunctions | null>;

export enum CalculationStrategies {
  LIAO = 'liao',
  LAMBDA = 'lambda',
  WATER_OIL_RELATIVE_PERMEABIILITY = 'water-oil',
  GAS_OIL_RELATIVE_PERMEABIILITY = 'gas-oil'
}

export type CorrelationEndpointSource = 'lab' | 'refinement';
export type CorrelationComparisonMode = 'off' | 'overlay';
export type CorrelationAppliesTo = 'water_oil' | 'gas_oil' | 'both';

export interface CorrelationPreferences {
  endpoint_source: CorrelationEndpointSource;
  comparison_mode: CorrelationComparisonMode;
  applies_to: CorrelationAppliesTo;
  x_variable?: XAxisVariable;
}

export type CorrelationSelectionOrigin =
  | 'manual'
  | 'auto'
  | 'auto_with_manual_adjustments';

export interface CorrelationSelectionState {
  origin: CorrelationSelectionOrigin;
  active_revision: number;
  zone_values_revision: number;
  auto_baseline_by_x_axis: Partial<
    Record<XAxisVariable, Record<string, SelectedFziFunctionsOptions | null>>
  >;
  manual_overrides_by_x_axis: Partial<
    Record<XAxisVariable, Record<string, SelectedFziFunctionsOptions | null>>
  >;
  applied_auto_report_uid: string | null;
}

interface CorrelationSamplesUidByType {
  [CalculationStrategies.LIAO]: string[];
  [CalculationStrategies.LAMBDA]: string[];
  [CalculationStrategies.WATER_OIL_RELATIVE_PERMEABIILITY]: string[];
  [CalculationStrategies.GAS_OIL_RELATIVE_PERMEABIILITY]: string[];
}

export interface HfuBand {
  label: string;
  cutoff: number | null;
  color: string;
}

export interface UpdateHfuBandsPayload {
  project_uid: string;
  hfu_bands: { cutoff: number | null; color: string }[];
}

export interface Correlation {
  uid: string;
  name: string;
  project_uid: string;
  samples_uid_by_type: CorrelationSamplesUidByType;
  wells_uid: string[];
  grad_diff: number;
  fzi_functions: FziFunctions | null;
  fzi_functions_by_x_axis?: FziFunctionsByXAxis | null;
  zones: Zone[];
  hfu_bands: HfuBand[];
  liao_correlation_values: LiaoCorrelation[] | null;
  lambda_correlation_values: LambdaCorrelation[] | null;
  gas_oil_relperm_correlations: GasOilCorrelation[] | null;
  water_oil_relperm_correlations: WaterOilCorrelation[] | null;
  preferences?: CorrelationPreferences | null;
  selection_state?: CorrelationSelectionState | null;
}

export interface GasOilCorrelation {
  sample_uid: string;
  fzi: number;
  rqi: number;
  sor_g: number;
  no_g: number;
  kro_absolute_g?: number | null;
  /** Present only in legacy reports that shared the Water-Oil endpoint. */
  kro_absolute?: number;
  krg_absolute: number;
  ng: number;
  sgc: number;
  sor: number;
  true_no_g?: number | null;
  true_ng?: number | null;
  correlation_type: string;
}
export interface WaterOilCorrelation {
  sample_uid: string;
  fzi: number;
  rqi: number;
  sor_w: number;
  no_w: number;
  kro_absolute: number;
  krw_absolute: number;
  nw: number;
  true_no_w?: number | null;
  true_nw?: number | null;
  correlation_type: string;
}

export interface LiaoCorrelation {
  sample_uid: string;
  fzi: number | null;
  rqi: number | null;
  swir: number | null;
  best_a: number | null;
  best_b: number | null;
  lambda_variable: number | null;
  true_pc_max: number | null;
  pce: number | null;
}

export interface LambdaCorrelation {
  sample_uid: string;
  fzi: number | null;
  rqi: number | null;
  lambda_value: number | null;
  pe: number | null;
}



export interface ProjectInformation {
  country: string | null;
  block: string | null;
  reservoir: string | null;
  rock_type: string | null;
  water_grad: number | null;
  oil_grad: number | null;
  lab_system: System;
  type_of_analyze: AnalyzeType;
}

export interface Project extends ProjectInformation {
  uid: string;
  project_name: string | null;
  users: User[];
  correlation: Correlation | null;
  version: number;
  conversion_parameters: ConversionParameters;
}

export interface AddProjectPayload {
  project_name?: string;
  country?: string;
  block?: string;
  reservoir?: string;
  rock_type?: string;
  water_grad?: number;
  oil_grad?: number;
  type_of_analyze?: AnalyzeType;
}

export interface GetProjectsResponse extends BaseResponse {
  content: Project[];
}

export interface AddProjectResponse extends BaseResponse {
  content: Project;
}

export interface AddUserToProjectResponse extends BaseResponse {
  content: Project;
}

export interface UpdateUserRoleFromProjectResponse extends BaseResponse {
  content: Project;
}

export interface EditProjectPayload extends AddProjectPayload {
  project_uid: string;
  zones?: Zone[];
  overwrite?: boolean;
  lab_system?: System;
}

export interface EditProjectResponse extends BaseResponse {
  content: Project;
}

export interface DeleteProjectResponse extends BaseResponse { }

export interface UpdateProjectSettingsPayload extends AddProjectPayload {
  project_uid: string;
  zones?: Zone[];
}

export interface UpdateProjectSettingsResponse extends BaseResponse {
  content: Project;
}

export interface RemoveUserResponse extends BaseResponse {
  content: Project;
}

export interface AddZoneToCorrelationPayload {
  project_uid: string;
  zones: { start: number; mean: number; end: number; color: string }[];
}

export interface AddZoneToCorrelationResponse extends BaseResponse {
  content: Project;
}

export interface DeleteZoneResponse extends BaseResponse {
  content: Project;
}

export interface CalculateCorrelationPayload {
  project_uid: string;
  liao_samples_uid?: string[];
  lambda_samples_uid?: string[];
  water_oil_samples_uid?: string[];
  gas_oil_samples_uid?: string[];
  x_variable?: "fzi" | "rqi";
  endpoint_source?: CorrelationEndpointSource;
  comparison_mode?: CorrelationComparisonMode;
}
export interface CalculateCorrelationResponse extends BaseResponse {
  content: Project;
}

export type SelectedFziFunctionsOptions =
  | 'linear'
  | 'power'
  | 'logarithmic'
  | 'exponential';

export type SelectedFziFunctions = {
  [key in keyof Partial<FziFunctions>]: SelectedFziFunctionsOptions;
};
export type CorrelationSection = 'capillary_pressure' | 'water_oil' | 'gas_oil';

export interface UpdateSelectedFziFunctionsPayload {
  x_variable?: XAxisVariable;
  selected: SelectedFziFunctions;
  section?: CorrelationSection;
}

export interface UpdateSelectedFziFunctionsResponse extends BaseResponse {
  content: Project;
}

export interface LiaoZoneValues {
  best_a?: number;
  best_b?: number;
  lambda_variable?: number;
  true_pc_max?: number;
  swir?: number;
  pce?: number;
  sor_w?: number;
  kro_absolute?: number;
  kro_absolute_g?: number;
  krw_absolute?: number;
  no_w?: number;
  nw?: number;
  no_g?: number;
  ng?: number;
  krg_absolute?: number;
  sgc?: number;
  sor?: number;
  sgmax?: number;
  sor_g?: number;
  pc_max_og?: number;
}

export interface LambdaZoneValues {
  pe?: number;
  lambda_value?: number;
  true_pc_max?: number;
  swir?: number;
  pce?: number;
  sor_w?: number;
  kro_absolute?: number;
  kro_absolute_g?: number;
  krw_absolute?: number;
  no_w?: number;
  nw?: number;
  no_g?: number;
  ng?: number;
  krg_absolute?: number;
  sgc?: number;
  sor?: number;
  sgmax?: number;
  sor_g?: number;
  pc_max_og?: number;
}

export interface UpdateZonesPayload {
  project_uid: string;
  user: User;
  zones: {
    zone_uid: string;
    zone_values: {
      liao_values?: LiaoZoneValues;
      lambda_values?: LambdaZoneValues;
    };
  }[];
}

export interface UpdateZoneBoundariesPayload {
  project_uid: string;
  zones: {
    zone_uid: string;
    start?: number;
    end?: number;
    mean?: number;
    color?: string;
  }[];
}

export interface ExportDataPayload {
  user: User;
  project_uid: string;
  scaled: boolean;
}

export interface ProjectPackageUser {
  uid: string;
  email: string;
  role: string;
}

export interface ProjectPackageCorrelation {
  uid: string;
  project_uid: string;
  wells_uid: string[];
  samples_uid_by_type: Record<string, string[]>;
  payload: Record<string, unknown>;
}

export interface ProjectPackageWell {
  uid: string;
  project_uid: string;
  well_name: string | null;
  payload: Record<string, unknown>;
}

export interface ProjectPackageSample {
  uid: string;
  project_uid: string;
  well_uid: string;
  sample_code: string | null;
  payload: Record<string, unknown>;
}

export interface ProjectPackage {
  format_version: string;
  exported_at: string;
  source_project_uid: string | null;
  project: {
    uid: string;
    project_name: string | null;
    users: ProjectPackageUser[];
    country: string | null;
    block: string | null;
    reservoir: string | null;
    rock_type: string | null;
    water_grad: number | null;
    oil_grad: number | null;
    type_of_analyze: string | null;
  };
  correlation: ProjectPackageCorrelation | null;
  wells: ProjectPackageWell[];
  samples: ProjectPackageSample[];
}

export interface DuplicateProjectPayload {
  project_name?: string;
}

export interface DuplicateProjectResponse extends BaseResponse {
  content: Project;
}

export interface ExportProjectPackageResponse {
  data: ProjectPackage;
  filename: string | null;
}

export interface ImportProjectPayload {
  file: File;
  project_name?: string;
}

export interface ImportProjectResponse extends BaseResponse {
  content: Project;
}

export interface ConvertUnitPayload {
  user: User;
  project_uid: string;
  depth_unit: "m" | "ft";
  pressure_unit: "psi" | "bar";
}

/* ── Auto Correlation ─────────────────────────────────────────────── */

export type AutoValidationSeverity = 'reject' | 'warning' | 'info';

export interface AutoValidationFlag {
  /** e.g. 'wrong_trend', 'out_of_bounds', 'low_r_squared' */
  code: string;
  severity: AutoValidationSeverity;
  message: string;
  context: Record<string, unknown>;
}

export interface AutoCandidateFit {
  tendency_type: SelectedFziFunctionsOptions;
  /** Coefficient (for logarithmic, c is the intercept). */
  c: number | null;
  b: number | null;
  r_squared: number | null;
  /** null => hard-rejected by a physical gate */
  score: number | null;
  /** zone_uid -> predicted value at zone.mean */
  predictions: Record<string, number | null>;
  /** x (string) -> predicted value at the reference x */
  reference_predictions: Record<string, number | null>;
  flags: AutoValidationFlag[];
}

export interface AutoParameterRecommendation {
  /** FziFunctions field name (e.g. 'swir') */
  parameter: string;
  /** All fitted candidates, scored. */
  candidates: AutoCandidateFit[];
  recommended: SelectedFziFunctionsOptions | null;
  best_r_squared: SelectedFziFunctionsOptions | null;
  sample_count: number;
  /** e.g. 'no_physical_candidate', 'very_few_samples' */
  flags: AutoValidationFlag[];
}

export type AutoCombinationKey = 'recommended' | 'best_r_squared' | 'current';

export interface AutoCombinationSet {
  key: AutoCombinationKey;
  selection: Record<string, SelectedFziFunctionsOptions | null>;
  /** zone_uid -> same shape as Zone.zone_values */
  zone_values: Record<string, ZoneValues>;
  /** zone_uid -> flags raised while simulating that zone's curves */
  curve_flags: Record<string, AutoValidationFlag[]>;
  /** e.g. 'repaired_selection', 'combination_invalid' */
  flags: AutoValidationFlag[];
  valid: boolean;
}

export type AutoCorrelationScopeMode =
  | 'all'
  | 'multi_analysis'
  | 'current_correlation'
  | 'manual'
  | 'filtered';

export interface AutoCorrelationScope {
  mode: AutoCorrelationScopeMode;
  filters: Record<string, unknown>;
  /** uids actually used (invalid ones dropped) */
  liao_samples_uid: string[];
  lambda_samples_uid: string[];
  water_oil_samples_uid: string[];
  gas_oil_samples_uid: string[];
  /** Missing in legacy reports, which are interpreted as lab. */
  endpoint_source?: CorrelationEndpointSource;
  family_availability?: Partial<
    Record<AutoCorrelationFamilyKey, AutoFamilyAvailability>
  >;
}

export type AutoCorrelationFamilyKey =
  | 'liao'
  | 'lambda'
  | 'water_oil'
  | 'gas_oil';

export interface AutoUnavailableSample {
  sample_uid: string;
  sample_code: string;
  missing_fields: string[];
}

export interface AutoFamilyAvailability {
  requested_count: number;
  usable_count: number;
  endpoint_source: CorrelationEndpointSource;
  unavailable_samples: AutoUnavailableSample[];
}

export interface AutoCorrelationReport {
  uid: string;
  /** ISO datetime */
  created_at: string | null;
  x_variable: XAxisVariable;
  grad_diff: number | null;
  scope: AutoCorrelationScope;
  parameters: Record<string, AutoParameterRecommendation>;
  /** [0] is always 'recommended' */
  combination_sets: AutoCombinationSet[];
  /** report-level (e.g. 'no_zones') */
  flags: AutoValidationFlag[];
  scoring_version: string;
  applied: boolean;
  applied_selection: Record<string, SelectedFziFunctionsOptions | null> | null;
  applied_x_variable?: XAxisVariable | null;
  applied_zone_values?: Record<string, ZoneValues>;
}

export interface RunAutoCorrelationPayload {
  x_variable?: XAxisVariable;
  /** Per-family uid lists; null/omitted = skip family. */
  liao_samples_uid?: string[] | null;
  lambda_samples_uid?: string[] | null;
  water_oil_samples_uid?: string[] | null;
  gas_oil_samples_uid?: string[] | null;
  grad_diff?: number;
  scope_mode?: AutoCorrelationScopeMode;
  /** Free-form echo of UI filters (audit only). */
  scope_filters?: Record<string, unknown>;
  reference_x_values?: number[] | null;
  endpoint_source?: CorrelationEndpointSource;
}

export interface RunAutoCorrelationResponse extends BaseResponse {
  content: AutoCorrelationReport;
}

export interface ApplyAutoCorrelationPayload {
  report_uid: string;
  x_variable: XAxisVariable;
  /** parameter -> tendency type (user overrides allowed) */
  selection: Record<string, SelectedFziFunctionsOptions>;
  /** Re-run POST /correlation with the report's sample scope first. */
  rerun_correlation: boolean;
  grad_diff: number;
}

export interface ApplyAutoCorrelationResponse extends BaseResponse {
  content: Project;
}

export interface GetLatestAutoCorrelationResponse extends BaseResponse {
  content: AutoCorrelationReport | null;
}
